import {
    useRef,
    useState
} from 'react'
import { useDialogContext } from '@shared/hooks/dialogContext'
import { useRestFetch } from '@shared/hooks/rest/fetch'
import { useMethods } from '@shared/hooks/methods'
import { useUser } from '@shared/hooks/user'
import * as Sentry from '@sentry/react'

export const useFields = () => {
    const { showMessage } = useMethods()
    const {
        form: {
            watch,
            setValue,
            control,
            getValues
        },
        instance,
        handleClose,
        dialogMode,

        loading,
        setLoading,

        viewDocs,
        viewCropper
    } = useDialogContext()
    const user = useUser()
    const urls = useRef([])
    const workType = watch('work_type')

    // tabs
    const [tab, setTab] = useState('general')
    
    // images
    const images = watch('images')
    const imgUploaderRef = useRef(null)
    const uploadQueueRef = useRef([])
    const isProcessingRef = useRef(false)

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/avif', 'image/bmp', 'image/tiff', 'image/gif']

    const getUserLocation = () => {
        return new Promise((resolve) => {
            setTimeout(() => resolve(null), 5000)
            if (!navigator.geolocation) return resolve(null)
            navigator.geolocation.getCurrentPosition(
                (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                () => resolve(null),
                {
                    timeout: 5000,
                    enableHighAccuracy: true
                }
            )
        })
    }

    const addWatermarkToImage = (file, location, user) => {
        return new Promise((resolve) => {
            const img = new Image()
            const url = URL.createObjectURL(file)
            img.onload = () => {
                const canvas = document.createElement('canvas')
                canvas.width = img.width
                canvas.height = img.height
                const ctx = canvas.getContext('2d')
                ctx.drawImage(img, 0, 0)

                const lines = []
                if (location) lines.push(`${location.lat.toFixed(5)}, ${location.lng.toFixed(5)}`)
                const dateStr = new Date().toLocaleString('ar-SA', {
                    calendar: 'gregory',
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                })
                lines.push(dateStr)

                if (getValues('district')) lines.push(getValues('district'))

                const userName = [user?.first_name, user?.last_name].filter(Boolean).join(' ')
                if (userName) lines.push(userName)

                if (lines.length > 0) {
                    const fontSize = Math.max(16, Math.floor(canvas.width * 0.025))
                    const lineHeight = fontSize * 1.3
                    const padding = fontSize * 0.6

                    ctx.font = `${fontSize}px sans-serif`
                    ctx.textAlign = 'right'
                    ctx.textBaseline = 'bottom'

                    let maxWidth = 0
                    lines.forEach((line) => {
                        const width = ctx.measureText(line).width
                        if (width > maxWidth) maxWidth = width
                    })

                    const boxWidth = maxWidth + padding * 2
                    const boxHeight = lines.length * lineHeight + padding * 2
                    const margin = fontSize

                    const boxX = canvas.width - margin - boxWidth
                    const boxY = canvas.height - margin - boxHeight

                    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)'
                    ctx.fillRect(boxX, boxY, boxWidth, boxHeight)

                    ctx.fillStyle = '#ffffff'
                    let currentY = canvas.height - margin - padding

                    for (let i = 0; i < lines.length; i++) {
                        ctx.fillText(lines[i], canvas.width - margin - padding, currentY)
                        currentY -= lineHeight
                    }
                }

                URL.revokeObjectURL(url)
                canvas.toBlob((blob) => {
                    if (!blob) {
                        resolve(file)
                        return
                    }
                    const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '')
                    const newFile = new File([blob], `${nameWithoutExt}.jpeg`, {
                        type: 'image/jpeg',
                        lastModified: Date.now()
                    })
                    resolve(newFile)
                }, 'image/jpeg', 0.95)
            }
            img.onerror = () => {
                URL.revokeObjectURL(url)
                resolve(file)
            }
            img.src = url
        })
    }

    const uploadImage = (e) => {
        const files = Array.from(e.target.files)
        imgUploaderRef.current.value = null

        const validFiles = files.filter(file => allowedTypes.includes(file.type))
        if (validFiles.length === 0) return

        uploadQueueRef.current.push(...validFiles.map(file => ({ file, dep: tab })))

        if (!isProcessingRef.current) {
            processQueue()
        }
    }

    const compress = async (file) => new Promise((resolve, reject) => {
        const img = document.createElement('img')
        const tempUrl = URL.createObjectURL(file)
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas')
                const ctx = canvas.getContext('2d')
                const maxSize = 1500

                let targetWidth=img.width
                let targetHeight=img.height

                if (img.width > img.height) {
                    targetWidth = maxSize
                    targetHeight *= maxSize / img.width
                } else {
                    targetWidth *= maxSize / img.height
                    targetHeight = maxSize
                }

                canvas.width = targetWidth
                canvas.height = targetHeight

                ctx.drawImage(img, 0, 0, targetWidth, targetHeight)
                URL.revokeObjectURL(tempUrl)
                canvas.toBlob(
                    resolve,
                    'image/jpeg',
                    0.75
                )
            } catch (e) {
                reject(e)
            }
        }
        img.onerror = () => {
            URL.revokeObjectURL(tempUrl)
            reject()
        }
        img.src = tempUrl
    })

    const processQueue = async () => {
        isProcessingRef.current = true
        setLoading(true)

        let location
        try {
            location = await getUserLocation()
        } catch (e) {
            Sentry.captureException(e, { extra: { context: 'Coordination getting error' } })
        }
        const failedFiles = []

        try {
            while (uploadQueueRef.current.length > 0) {
                const remainingBefore = uploadQueueRef.current.length
                const batch = uploadQueueRef.current.splice(0, 7)

                showMessage({
                    text: `جارٍ ضغط الصور... (متبقي ${remainingBefore} صورة)`,
                    loading: true
                })

                const batchResults = await Promise.all(
                    batch.map(async ({ file, dep }) => {
                        try {
                            const compressedBlob = await compress(file)
                            const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '')
                            const newFileName = `${nameWithoutExt}.jpeg`
                            const compressedFile = new File([compressedBlob], newFileName, {
                                type: 'image/jpeg',
                                lastModified: Date.now(),
                            })
                            const finalFile = dep !== 'documents'
                                ? await addWatermarkToImage(compressedFile, location, user)
                                : compressedFile
                            const url = URL.createObjectURL(finalFile)
                            return { id: crypto.randomUUID(), file: finalFile, url, dep }
                        } catch (err) {
                            console.error('فشل ضغط الصورة:', file.name, err)
                            Sentry.captureException(err, { extra: { context: 'Upload error', fileName: file.name, fileSize: file.size } })
                            failedFiles.push(file.name)
                            return null
                        }
                    })
                )

                const successResults = batchResults.filter(Boolean)
                if (successResults.length > 0) {
                    const newUrls = successResults.map(item => item.url)
                    urls.current = [...urls.current, ...newUrls]

                    const currentImages = getValues('images') || []
                    setValue('images', [...currentImages, ...successResults], { shouldDirty: true })
                }
            }

            if (failedFiles.length > 0) {
                showMessage({
                    text: `تعذر رفع ${failedFiles.length} صورة. حاول رفعها مرة أخرى.`,
                    loading: false
                })
            }
        } catch (error) {
            console.error('حدث خطأ أثناء ضغط الصور:', error)
            showMessage({ text: 'حدث خطأ أثناء معالجة الصور', loading: false })
        } finally {
            isProcessingRef.current = false
            if (failedFiles.length === 0) showMessage(null)
            setLoading(false)
        }
    }

    // attachments
    const attachments = watch('attachments')
    const atchUploaderRef = useRef(null)

    const uploadAttachment = (e) => {
        setLoading(true)
        const files = Array.from(e.target.files)
        const MAX_SIZE = 100 * 1024 * 1024 // 100 MB
        const validFiles = files.filter((file) => {
            if (file.type !== 'application/pdf') {
                showMessage({ text: 'ما اخترته ليس pdf.' })
                return false
            }
            if (file.size > MAX_SIZE) {
                showMessage({ text: 'حجم الملف كبير، الحد ١٠٠ ميغابايت' })
                return false
            }
            return true
        })

        const newAttachments = validFiles.map((file) => {
            const url = URL.createObjectURL(file)
            urls.current = [...urls.current, url]
            return {
                id: crypto.randomUUID(),
                file,
                name: file.name,
                size: file.size,
                url
            }
        })

        setValue('attachments', [...attachments, ...newAttachments], { shouldDirty: true })
        atchUploaderRef.current.value = null
        setLoading(false)
    }

    // fetches
    const offices = useRestFetch({
        key: ['offices'],
        endpoint: 'settings/offices/',
        retry: Infinity
    })
    const delegates = useRestFetch({
        key: ['pdc-delegates'],
        endpoint: 'settings/pdc-delegates/',
        retry: Infinity
    })

    return {
        instance,
        handleClose,
        workType,
        control,
        dialogMode,
        loading,

        tab,
        setTab,

        viewDocs,
        viewCropper,

        images,
        imgUploaderRef,
        uploadImage,

        attachments,
        atchUploaderRef,
        uploadAttachment,

        offices,
        delegates
    }
}